import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { enqueue } from '../../offline/queueOps'
import { cachedMachineName, describeShiftId } from '../../offline/describeShiftId'
import Button from '../Buttons/Button'
import type { OfflineOperation } from '../../offline/db'
import type { LocationDTO } from '../../DTOs/Production/LocationDTO'
import type { MaterialTypeClassDTO } from '../../DTOs/MaterialTypeClassDTO'

interface Props {
  issue: SyncIssue
  onClose: () => void
}

type Resolvers = {
  location: (id: unknown) => string
  materialClass: (id: unknown) => string
  digger: (id: unknown) => string
  stoppageSubType: (id: unknown) => string
}

type FieldSpec = { key: string; label: string; format?: (v: unknown, r: Resolvers) => string }

// Note: only the fields that actually help a human decide which version to keep. Everything else
// (system columns like rowVersion / lastEdited, computed columns like density / tonnes-derived
// values, and raw parent ids) is intentionally omitted - see the fallback filter below.
const FIELD_SPECS: Partial<Record<SyncIssue['entity'], FieldSpec[]>> = {
  // Note: material type, tonnes and density are derived server-side (from the source stockpile /
  // bucket factor), not set by the crushing/rehandle client, so they are deliberately not shown as
  // conflict fields - the client always sends them null/stale and the server re-derives them on
  // save, so displaying them would falsely imply the user was changing them.
  geoRehandle: [
    { key: 'operationId', label: 'Digger / Loader', format: (v, r) => r.digger(v) },
    { key: 'loads', label: 'Loads' },
    { key: 'movementFromId', label: 'Movement From', format: (v, r) => r.location(v) },
    { key: 'movementToId', label: 'Movement To', format: (v, r) => r.location(v) },
    { key: 'hour', label: 'Hour' },
  ],
  loading: [
    { key: 'loads', label: 'Loads' },
    { key: 'destinationId', label: 'Destination', format: (v, r) => r.location(v) },
    { key: 'materialTypeClassId', label: 'Material Type', format: (v, r) => r.materialClass(v) },
  ],
  stoppage: [
    { key: 'stoppageSubTypeId', label: 'Breakdown Code', format: (v, r) => r.stoppageSubType(v) },
    { key: 'locationId', label: 'Location', format: (v, r) => r.location(v) },
    { key: 'workOrder', label: 'Work Order' },
    { key: 'repairsFaults', label: 'Repairs / Faults' },
    { key: 'incident', label: 'Incident' },
  ],
}

// Note: for entities without an explicit spec, show plain scalar fields but never dump objects
// (e.g. a serialised date) or noisy system columns.
const HIDDEN_KEYS = new Set([
  'rowVersion', 'lastEdited', 'density', 'wetTonnes', 'adjTonnesHauled',
  'operationId', 'officerId', 'spotterId', 'bucketTypeId',
  'geoRehandleId', 'loadingId', 'diggingPatternId', 'plantId',
])

function scalar(val: unknown): string {
  if (val === null || val === undefined) return '—'
  if (typeof val === 'object') return '—'
  return String(val)
}

function displayValue(spec: FieldSpec | undefined, value: unknown, resolvers: Resolvers): string {
  return spec?.format ? spec.format(value, resolvers) : scalar(value)
}

function FieldDiff({ label, client, server }: { label: string; client: string; server: string }) {
  const isDiff = client !== server
  return (
    <tr className={isDiff ? 'bg-theme-warning/10' : ''}>
      <td className="py-1 pr-3 text-xs font-medium text-theme-text-muted whitespace-nowrap">{label}</td>
      <td className={`py-1 pr-3 text-xs font-mono ${isDiff ? 'text-theme-warning font-bold' : 'text-theme-text'}`}>
        {client}
      </td>
      <td className="py-1 pr-1 shrink-0 text-theme-text-muted">
        {isDiff && <FontAwesomeIcon icon={faArrowRight} className="text-[0.6rem]" />}
      </td>
      <td className={`py-1 text-xs font-mono ${isDiff ? 'text-theme-success font-bold' : 'text-theme-text'}`}>
        {server}
      </td>
    </tr>
  )
}

function serverEntityId(issue: SyncIssue): number | undefined {
  const s = issue.serverState as Record<string, unknown> | null
  if (!s) return undefined
  const key = `${issue.entity}Id`
  return s[key] as number | undefined
}

async function keepMine(issue: SyncIssue, onClose: () => void) {
  const server = issue.serverState as Record<string, unknown> | null
  const serverRowVersion = server?.rowVersion as string | undefined
  const resolvedEntityId = issue.entityId ?? serverEntityId(issue)
  const resolvedOperation: OfflineOperation = issue.operation === 'create' && resolvedEntityId != null ? 'update' : issue.operation as OfflineOperation
  await enqueue({
    entity: issue.entity,
    operation: resolvedOperation,
    entityId: resolvedEntityId,
    shiftId: issue.shiftId ?? 0,
    rowVersion: serverRowVersion,
    // Note: "keep mine" reapplies my field values on top of the CURRENT server row, so the
    // rowVersion embedded in the data must be the server's latest too. The repository update
    // re-checks the payload's own rowVersion, so leaving the original (stale) one here would fail
    // that inner concurrency check even though the sync-level gate already passed.
    data: { ...(issue.clientData as Record<string, unknown> | null ?? {}), rowVersion: serverRowVersion },
  })
  await offlineDb.syncIssues.delete(issue.id!)
  onClose()
}

async function keepTheirs(issue: SyncIssue, onClose: () => void) {
  await offlineDb.syncIssues.delete(issue.id!)
  onClose()
}

const ConflictResolutionModal = ({ issue, onClose }: Props) => {
  const clientObj = issue.clientData as Record<string, unknown> | null
  const serverObj = issue.serverState as Record<string, unknown> | null

  const queryClient = useQueryClient()
  const resolvers = useMemo<Resolvers>(() => {
    const locations = (queryClient.getQueryData(['references', 'locations']) as LocationDTO[] | undefined) ?? []
    const classes = (queryClient.getQueryData(['references', 'materialTypeClasses', { isLive: true }]) as MaterialTypeClassDTO[] | undefined) ?? []
    const locMap = new Map(locations.map(l => [l.id, l.name]))
    const classMap = new Map(classes.map(c => [c.id, c.class]))
    // Note: the Digger (rehandle) / Loader (crushing) is the row's operationId, and its label lives
    // in the shift-scoped digger/loader option lists. Gather every cached variant of those (any
    // date/shift) and map operationId -> plantRef so the conflicting field can be shown by name.
    const opMap = new Map<number, string>()
    const optionSets = [
      ...queryClient.getQueriesData({ queryKey: ['geology', 'rehandleDiggers'] }),
      ...queryClient.getQueriesData({ queryKey: ['geology', 'crushingLoaders'] }),
    ]
    for (const [, data] of optionSets) {
      if (!Array.isArray(data)) continue
      for (const d of data as Array<{ operationId?: number; plantRef?: string }>) {
        if (d && d.operationId != null && d.plantRef != null) opMap.set(Number(d.operationId), String(d.plantRef))
      }
    }
    // Note: the Breakdown Code is the stoppage sub type; map its id to the name from the cached
    // all-sub-types list (warmed by the prefetch and BreakdownCodeCell).
    const subTypes = (queryClient.getQueryData(['references', 'stoppageSubTypes']) as Array<{ stoppageSubTypeId?: number; stoppageSubType?: string }> | undefined) ?? []
    const subTypeMap = new Map(subTypes.map(s => [s.stoppageSubTypeId, s.stoppageSubType]))
    return {
      location: id => id == null ? '—' : (locMap.get(Number(id)) ?? `#${id}`),
      materialClass: id => id == null ? '—' : (classMap.get(Number(id)) ?? `#${id}`),
      digger: id => id == null ? '—' : (opMap.get(Number(id)) ?? `#${id}`),
      stoppageSubType: id => id == null ? '—' : (subTypeMap.get(Number(id)) ?? `#${id}`),
    }
  }, [queryClient])

  const rows = useMemo(() => {
    const spec = FIELD_SPECS[issue.entity]
    const specs: FieldSpec[] = spec ?? Array.from(new Set([
      ...Object.keys(clientObj ?? {}),
      ...Object.keys(serverObj ?? {}),
    ]))
      .filter(k => !HIDDEN_KEYS.has(k) && typeof (clientObj?.[k] ?? serverObj?.[k]) !== 'object')
      .map(k => ({ key: k, label: k }))

    return specs.map(s => ({
      label: s.label,
      client: displayValue(s, clientObj?.[s.key], resolvers),
      server: displayValue(s, serverObj?.[s.key], resolvers),
    }))
  }, [issue.entity, clientObj, serverObj, resolvers])

  const entityLabel = issue.entity === 'diggingPattern' ? 'digging location'
    : issue.entity === 'patternBlast' ? 'blast'
    : issue.entity === 'loading' ? 'truck load'
    : issue.entity === 'geoRehandle' ? 'crushing / rehandle entry'
    : issue.entity === 'completeShift' ? 'shift completion'
    : issue.entity

  // Note: which machine and which shift the change belongs to. Without it the modal asked the crew to
  // choose between two versions of something, with nothing to say what that something was.
  const context = useMemo(() => {
    const rowFor = (source: unknown) => (source ?? {}) as { plantId?: number; plant?: { plantRef?: string }; startTime?: unknown }
    const server = rowFor(issue.serverState)
    const client = rowFor(issue.clientData)
    const machine = server.plant?.plantRef
      ?? client.plant?.plantRef
      ?? cachedMachineName(server.plantId ?? client.plantId)
    const shift = describeShiftId(issue.shiftId)
    const started = typeof server.startTime === 'string' || typeof client.startTime === 'string'
      ? DateTime.fromISO(String(server.startTime ?? client.startTime))
      : null

    return [
      machine,
      shift,
      started?.isValid ? `started ${started.toFormat('HH:mm')}` : null,
    ].filter(Boolean).join(' · ') || null
  }, [issue])

  return (
    <div className="flex flex-col gap-4 p-2">
      <p className="text-theme-text text-sm">
        This {entityLabel} was edited by someone else while you were offline. Choose how to resolve:
      </p>

      {context && (
        <p className="text-theme-text-muted text-xs">{context}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="py-1 pr-3 text-xs font-semibold text-theme-text-muted">Field</th>
              <th className="py-1 pr-3 text-xs font-semibold text-theme-text">Your version</th>
              <th className="py-1 pr-1" />
              <th className="py-1 text-xs font-semibold text-theme-text">Server version</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <FieldDiff key={r.label} label={r.label} client={r.client} server={r.server} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 justify-end flex-wrap">
        <Button variant="primary" size="sm" onClick={() => keepMine(issue, onClose)}>
          Keep mine
        </Button>
        <Button variant="secondary" size="sm" onClick={() => keepTheirs(issue, onClose)}>
          Keep theirs
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Dismiss
        </Button>
      </div>
    </div>
  )
}

export default ConflictResolutionModal
